import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Shield, Building2, Plus, Trash2, Search, RefreshCw,
  Loader2, UserPlus, ChevronRight, ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_DISPLAY_NAMES = {
  system_admin: 'System Administrator',
  admin: 'Administrator',
  tmf_lead: 'TMF Lead',
  clinical_monitor: 'Clinical Monitor',
  data_manager: 'Data Manager',
  site_user: 'Site User',
  quality_assurance: 'Quality Assurance',
  qc_reviewer: 'QC Reviewer',
  regulatory_affairs: 'Regulatory Affairs',
  sponsor_representative: 'Sponsor Representative',
  inspector: 'Inspector'
};

export default function UserStudyAssignmentsPanel() {
  const { getAuthHeaders } = useAuth();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [studies, setStudies] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userAssignments, setUserAssignments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Assignment dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [newAssignment, setNewAssignment] = useState({ study_id: '', role: '' });
  
  // Expanded users for inline view
  const [expandedUsers, setExpandedUsers] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();
      const [usersRes, studiesRes, rolesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { headers }),
        axios.get(`${API_URL}/api/studies`, { headers }),
        axios.get(`${API_URL}/api/user-study-assignments/available-roles`, { headers })
      ]);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || []));
      setStudies(Array.isArray(studiesRes.data) ? studiesRes.data : (studiesRes.data?.studies || []));
      setAvailableRoles(rolesRes.data?.roles || []);
      
      // Set default role if available
      if (rolesRes.data?.roles?.length > 0) {
        setNewAssignment(prev => ({ ...prev, role: rolesRes.data.roles[0].id }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchUserAssignments = async (userId) => {
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/user-study-assignments/user/${userId}`, { headers });
      setUserAssignments(response.data?.assignments || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast.error('Failed to load user assignments');
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    await fetchUserAssignments(user.id);
  };

  const toggleUserExpanded = async (userId) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
      // Fetch assignments for this user
      const { headers } = getAuthHeaders();
      try {
        const response = await axios.get(`${API_URL}/api/user-study-assignments/user/${userId}`, { headers });
        setUsers(prev => prev.map(u => 
          u.id === userId ? { ...u, _assignments: response.data?.assignments || [] } : u
        ));
      } catch (error) {
        console.error('Error fetching assignments:', error);
      }
    }
    setExpandedUsers(newExpanded);
  };

  const handleAddAssignment = async () => {
    if (!selectedUser || !newAssignment.study_id || !newAssignment.role) {
      toast.error('Please select a study and role');
      return;
    }

    setAssignLoading(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/user-study-assignments`, {
        user_id: selectedUser.id,
        study_id: newAssignment.study_id,
        role: newAssignment.role
      }, { headers });
      
      toast.success('Assignment created successfully');
      setShowAssignDialog(false);
      setNewAssignment({ study_id: '', role: 'site_user' });
      await fetchUserAssignments(selectedUser.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleDeleteAssignment = async (userId, studyId) => {
    try {
      const { headers } = getAuthHeaders();
      await axios.delete(`${API_URL}/api/user-study-assignments/${userId}/${studyId}`, { headers });
      toast.success('Assignment removed');
      
      if (selectedUser?.id === userId) {
        await fetchUserAssignments(userId);
      }
      
      // Update inline expanded view
      if (expandedUsers.has(userId)) {
        const response = await axios.get(`${API_URL}/api/user-study-assignments/user/${userId}`, { headers });
        setUsers(prev => prev.map(u => 
          u.id === userId ? { ...u, _assignments: response.data?.assignments || [] } : u
        ));
      }
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return u.name?.toLowerCase().includes(query) || u.email?.toLowerCase().includes(query);
  });

  // Get all studies for admin - mark which ones are already assigned
  const allStudiesWithStatus = studies.map(s => ({
    ...s,
    isAssigned: userAssignments.some(a => a.study_id === s.id),
    currentRole: userAssignments.find(a => a.study_id === s.id)?.role
  }));
  
  // For backward compatibility
  const availableStudies = studies;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">User Study Assignments</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Assign users to studies with specific roles
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="border-slate-300"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-6">
          {/* Users List */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-teal-600" />
                Users ({filteredUsers.length})
              </CardTitle>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-96">
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {filteredUsers.map((user) => (
                    <div key={user.id}>
                      <div
                        className={`flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${
                          selectedUser?.id === user.id ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                        }`}
                        onClick={() => handleSelectUser(user)}
                      >
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleUserExpanded(user.id); }}
                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                          >
                            {expandedUsers.has(user.id) ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                          </button>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{user.name || user.email}</p>
                            <p className="text-xs text-slate-500">{user.email}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {ROLE_DISPLAY_NAMES[user.role] || user.role}
                        </Badge>
                      </div>
                      
                      {/* Inline assignments */}
                      {expandedUsers.has(user.id) && user._assignments && (
                        <div className="pl-12 pr-4 py-2 bg-slate-50 dark:bg-slate-800/50 space-y-1">
                          {user._assignments.length === 0 ? (
                            <p className="text-xs text-slate-500 py-2">No study assignments</p>
                          ) : (
                            user._assignments.map((a) => (
                              <div key={`${a.user_id}-${a.study_id}`} className="flex items-center justify-between text-xs py-1">
                                <span className="text-slate-700 dark:text-slate-300">{a.study?.name}</span>
                                <div className="flex items-center gap-2">
                                  <Badge className="text-[10px] bg-teal-100 text-teal-700">
                                    {ROLE_DISPLAY_NAMES[a.role] || a.role}
                                  </Badge>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteAssignment(user.id, a.study_id); }}
                                    className="p-1 text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Selected User Assignments */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-teal-600" />
                  Study Assignments
                </CardTitle>
                {selectedUser && (
                  <Button
                    size="sm"
                    onClick={() => setShowAssignDialog(true)}
                    className="bg-teal-600 hover:bg-teal-700"
                    disabled={availableStudies.length === 0}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Assignment
                  </Button>
                )}
              </div>
              {selectedUser && (
                <CardDescription>
                  Managing assignments for <strong>{selectedUser.name || selectedUser.email}</strong>
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {!selectedUser ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Select a user to view their study assignments</p>
                </div>
              ) : userAssignments.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">No study assignments yet</p>
                  <Button
                    size="sm"
                    onClick={() => setShowAssignDialog(true)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Assign to Study
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-80">
                  <div className="space-y-2">
                    {userAssignments.map((assignment) => (
                      <div
                        key={`${assignment.user_id}-${assignment.study_id}`}
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 dark:text-white">
                            {assignment.study?.name || 'Unknown Study'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {assignment.study?.protocol_number || 'No Protocol'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
                            {ROLE_DISPLAY_NAMES[assignment.role] || assignment.role}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAssignment(selectedUser.id, assignment.study_id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-white dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle>Assign User to Study</DialogTitle>
            <DialogDescription>
              Select a study and role for {selectedUser?.name || selectedUser?.email}. 
              You can assign to a new study or update an existing assignment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Study *</Label>
              <Select 
                value={newAssignment.study_id} 
                onValueChange={(v) => {
                  const study = allStudiesWithStatus.find(s => s.id === v);
                  setNewAssignment(p => ({ 
                    ...p, 
                    study_id: v,
                    // Pre-fill role if already assigned
                    role: study?.currentRole || p.role
                  }));
                }}
              >
                <SelectTrigger data-testid="select-study">
                  <SelectValue placeholder="Select a study" />
                </SelectTrigger>
                <SelectContent>
                  {allStudiesWithStatus.map((study) => (
                    <SelectItem key={study.id} value={study.id}>
                      <span className="flex items-center gap-2">
                        {study.name} {study.protocol_number ? `(${study.protocol_number})` : ''}
                        {study.isAssigned && (
                          <span className="text-xs text-teal-600 ml-1">
                            • Currently: {ROLE_DISPLAY_NAMES[study.currentRole] || study.currentRole}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {allStudiesWithStatus.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No studies available</p>
              )}
            </div>
            
            <div>
              <Label>Role for this Study *</Label>
              <Select 
                value={newAssignment.role} 
                onValueChange={(v) => setNewAssignment(p => ({ ...p, role: v }))}
              >
                <SelectTrigger data-testid="select-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableRoles.length === 0 && (
                <p className="text-xs text-red-600 mt-1">You do not have permission to assign roles</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddAssignment} 
              disabled={assignLoading || !newAssignment.study_id || !newAssignment.role || availableRoles.length === 0}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="submit-assignment"
            >
              {assignLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
